import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import AdminNavTabs from '../components/AdminNavTabs';
import { useAdminCapability, CONTROLLABLE_TABS, LEVELS } from '../components/useAdminCapability';
import './AdminUserManagement.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// A Staff permission level counts as "granted" when it is View or Edit. None
// (or a missing tab key) means no access, mirroring the backend default.
function isGranted(level) {
  return level === 'View' || level === 'Edit';
}

// Returns the ordered list of { tab, level } grants for a Staff user, using the
// canonical Controllable Tab Set ordering so the summary reads consistently.
function grantedTabs(permissions) {
  const perms = permissions && typeof permissions === 'object' ? permissions : {};
  return CONTROLLABLE_TABS.map((tab) => ({ tab, level: perms[tab] || 'None' })).filter((p) =>
    isGranted(p.level)
  );
}

/**
 * StaffPermissionSummary
 *
 * Expandable per-row summary of a Staff user's granted Controllable_Tabs and
 * their Permission_Levels (Requirement 9.5). Admin/Superadmin rows render a
 * short note instead, since they have full access and no per-tab matrix
 * (design §6). This is a self-contained presentational component so the later
 * edit-permissions control (task 11.4) can reuse or replace it cleanly.
 */
function StaffPermissionSummary({ role, permissions }) {
  const [open, setOpen] = useState(false);

  if (role !== 'Staff') {
    return <span className="um-perm-na">Full access — no per-tab permissions</span>;
  }

  const grants = grantedTabs(permissions);

  return (
    <div className="um-perm-summary">
      <button
        type="button"
        className="um-perm-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} aria-hidden="true"></i>
        {grants.length === 0
          ? 'No tabs granted'
          : `${grants.length} tab${grants.length > 1 ? 's' : ''} granted`}
      </button>
      {open && (
        <div className="um-perm-detail">
          {grants.length === 0 ? (
            <p className="um-perm-empty">This Staff user has no Controllable Tabs granted.</p>
          ) : (
            <ul className="um-perm-list">
              {grants.map(({ tab, level }) => (
                <li key={tab} className="um-perm-item">
                  <span className="um-perm-tab">{tab}</span>
                  <span className={`um-level-badge um-level-${level.toLowerCase()}`}>{level}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const ROLE_OPTIONS = ['Superadmin', 'Admin', 'Staff'];

// Builds the default Staff permission set: every Controllable_Tab at None
// (Req 10.7). Used to seed the matrix when the dialog opens or when the role
// switches to Staff so the UI mirrors the backend default.
function defaultStaffPermissions() {
  return CONTROLLABLE_TABS.reduce((acc, tab) => {
    acc[tab] = 'None';
    return acc;
  }, {});
}

// Normalizes an arbitrary permissions object into a complete Staff permission
// set over the canonical CONTROLLABLE_TABS, defaulting any missing/unknown tab
// to None. Used to seed the edit matrix from a user's stored grants so the UI
// always shows every controllable tab (Req 11.3/11.4).
function normalizeStaffPermissions(permissions) {
  const perms = permissions && typeof permissions === 'object' ? permissions : {};
  return CONTROLLABLE_TABS.reduce((acc, tab) => {
    acc[tab] = LEVELS.includes(perms[tab]) ? perms[tab] : 'None';
    return acc;
  }, {});
}

// Shallow-compares two permission sets over the canonical tab list (treating a
// missing tab as None) so the edit dialog can tell whether the Staff grant map
// actually changed and skip a no-op PUT .../permissions call.
function permissionsEqual(a, b) {
  const pa = a && typeof a === 'object' ? a : {};
  const pb = b && typeof b === 'object' ? b : {};
  return CONTROLLABLE_TABS.every((tab) => (pa[tab] || 'None') === (pb[tab] || 'None'));
}

/**
 * PermissionMatrix
 *
 * The canonical tab × {None/View/Edit} permission matrix shared by the Add User
 * and Edit dialogs (design §6). Renders one row per Controllable_Tab with a
 * radio group over the ordered LEVELS, and reports changes through onChange.
 * Behavior is identical to the original inline Add User matrix; `namePrefix`
 * keeps radio-group names unique per dialog instance.
 */
function PermissionMatrix({ permissions, onChange, disabled, namePrefix = 'perm' }) {
  return (
    <div className="um-field">
      <span className="um-matrix-label">Tab permissions</span>
      <div className="um-matrix">
        {CONTROLLABLE_TABS.map((tab) => (
          <div className="um-matrix-row" key={tab}>
            <span className="um-matrix-tab">{tab}</span>
            <div
              className="um-matrix-levels"
              role="radiogroup"
              aria-label={`${tab} permission level`}
            >
              {LEVELS.map((level) => (
                <label key={level} className="um-matrix-level">
                  <input
                    type="radio"
                    name={`${namePrefix}-${tab}`}
                    value={level}
                    checked={(permissions[tab] || 'None') === level}
                    onChange={() => onChange(tab, level)}
                    disabled={disabled}
                  />
                  <span>{level}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Maps a backend error code to a human-readable message. Unknown codes fall
// back to the server-provided message (if any) or a generic failure string so
// the Superadmin always sees something actionable (Req 10.5, 10.6).
function describeCreateError(code, message) {
  switch (code) {
    case 'DUPLICATE_EMAIL':
      return 'A user with that email already exists in this tenant.';
    case 'INVALID_EMAIL':
      return 'That email address is not valid. Please check and try again.';
    case 'DUPLICATE_USERNAME':
      return 'That username is already taken. Please choose a different one.';
    case 'INVALID_USERNAME':
      return 'Username must be 3-64 characters: letters, digits, dot, underscore, or hyphen (no spaces or @).';
    default:
      return message || 'Failed to create user. Please try again.';
  }
}

/**
 * AddUserDialog
 *
 * Modal for creating a new Managed_User via POST /admin/users. Collects an
 * email, a role (Superadmin / Admin / Staff), and — only when the role is
 * Staff — a per-tab permission matrix over the canonical CONTROLLABLE_TABS.
 * Submits { email, role, permissions } (permissions omitted for non-Staff),
 * surfaces API errors (DUPLICATE_EMAIL / INVALID_EMAIL / validation) inline,
 * and on success closes and asks the parent to refresh the list.
 *
 * Authorization is enforced server-side; this dialog is a convenience layer.
 */
function AddUserDialog({ open, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [usernameInput, setUsernameInput] = useState('');
  const [role, setRole] = useState('Staff');
  const [permissions, setPermissions] = useState(() => defaultStaffPermissions());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const emailRef = useRef(null);

  // Reset the form each time the dialog is (re)opened so a previous attempt's
  // values or error never leak into a fresh entry.
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setUsernameInput('');
    setRole('Staff');
    setPermissions(defaultStaffPermissions());
    setSubmitting(false);
    setFormError('');
    try {
      window.setTimeout(() => emailRef.current?.focus?.(), 50);
    } catch {
      // ignore
    }
  }, [open]);

  if (!open) return null;

  const setTabLevel = (tab, level) => {
    setPermissions((prev) => ({ ...prev, [tab]: level }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError('Email is required.');
      emailRef.current?.focus?.();
      return;
    }

    try {
      setSubmitting(true);

      if (!API_BASE_URL) {
        throw new Error('Admin API base URL is not configured');
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // Permissions are only meaningful for Staff; Admin/Superadmin have full
      // access and carry no per-tab matrix (design §6, Req 10.7).
      const body = { email: trimmedEmail, role };
      const trimmedUsername = usernameInput.trim();
      if (trimmedUsername) {
        body.username = trimmedUsername;
      }
      if (role === 'Staff') {
        body.permissions = permissions;
      }

      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        setFormError(describeCreateError(data?.code, data?.error));
        return;
      }

      onCreated();
    } catch (err) {
      console.error('Error creating user:', err);
      setFormError(err.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="um-modal-overlay" role="dialog" aria-modal="true" aria-label="Add user">
      <div className="um-modal">
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>Add User</h3>
            <button
              type="button"
              className="um-modal-close"
              onClick={onClose}
              aria-label="Close"
              disabled={submitting}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div className="um-modal-body">
            {formError && (
              <div className="alert alert-error" role="alert" aria-live="assertive">
                <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                {formError}
              </div>
            )}

            <div className="um-field">
              <label htmlFor="um-add-email">Email</label>
              <input
                id="um-add-email"
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                autoComplete="off"
                disabled={submitting}
              />
            </div>

            <div className="um-field">
              <label htmlFor="um-add-username">Username (optional)</label>
              <input
                id="um-add-username"
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                placeholder="e.g. franc.len (leave blank to auto-generate)"
                autoComplete="off"
                disabled={submitting}
              />
              <p className="um-field-hint">
                A friendly sign-in handle. 3-64 characters: letters, digits, dot,
                underscore, or hyphen. If left blank, one is generated automatically.
                The user can also sign in with their email.
              </p>
            </div>

            <div className="um-field">
              <label htmlFor="um-add-role">Role</label>
              <select
                id="um-add-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={submitting}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <p className="um-field-hint">
                {role === 'Staff'
                  ? 'Staff users only access the tabs you grant below.'
                  : `${role}s have full access to all tabs${
                      role === 'Admin' ? ' except User Management' : ''
                    }.`}
              </p>
            </div>

            {role === 'Staff' && (
              <PermissionMatrix
                permissions={permissions}
                onChange={setTabLevel}
                disabled={submitting}
                namePrefix="add-perm"
              />
            )}
          </div>

          <div className="um-modal-footer">
            <button
              type="button"
              className="um-btn um-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="um-btn um-btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Creating…
                </>
              ) : (
                'Create User'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Maps a backend error code from the edit (role / permissions) endpoints to a
// human-readable message. Covers the safety-rail and validation codes from
// design §3 (SELF_ROLE_CHANGE, SELF_MODIFICATION, LAST_SUPERADMIN, INVALID_TAB,
// INVALID_LEVEL, INVALID_ROLE); unknown codes fall back to the server message.
function describeEditError(code, message) {
  switch (code) {
    case 'SELF_ROLE_CHANGE':
      return 'You cannot change your own role.';
    case 'SELF_MODIFICATION':
      return 'You cannot modify your own account.';
    case 'LAST_SUPERADMIN':
      return 'This tenant must keep at least one Superadmin, so this change was blocked.';
    case 'INVALID_TAB':
      return 'One of the selected tabs is not a valid controllable tab.';
    case 'INVALID_LEVEL':
      return 'One of the selected permission levels is not valid.';
    case 'INVALID_ROLE':
      return 'That role is not valid.';
    default:
      return message || 'Failed to save changes. Please try again.';
  }
}

// Maps a backend error code from the row-level account actions (disable / enable
// / hard delete) to a human-readable message, mirroring describeEditError's
// safety-rail wording (design §3). CONFIRM_REQUIRED only applies to delete and
// should never surface in practice since the confirmation modal always sends
// { confirm: true }; it's mapped defensively. Unknown codes fall back to the
// server message.
function describeActionError(code, message) {
  switch (code) {
    case 'SELF_MODIFICATION':
      return 'You cannot modify your own account.';
    case 'LAST_SUPERADMIN':
      return 'This tenant must keep at least one Superadmin, so this change was blocked.';
    case 'CONFIRM_REQUIRED':
      return 'Deleting a user requires explicit confirmation.';
    default:
      return message || 'Failed to update the user. Please try again.';
  }
}

/**
 * EditUserDialog
 *
 * Modal for editing an existing Managed_User's role and (for Staff) per-tab
 * permissions. Opens seeded from the selected user's current role and grants.
 * The role select offers Superadmin / Admin / Staff; the permission matrix only
 * shows when the (newly chosen) role is Staff.
 *
 * Wiring (design §3, Req 11.1/11.3–11.6):
 *  - When the role changed, submit PUT /admin/users/{username}/role with
 *    { role, permissions? } — the role endpoint applies the Staff permission
 *    set in the same call, so a Staff-bound change is atomic.
 *  - When the role is unchanged and stays Staff, submit only the changed grant
 *    map via PUT /admin/users/{username}/permissions.
 *  - When nothing changed, close without a network call.
 *
 * API errors (SELF_ROLE_CHANGE, SELF_MODIFICATION, LAST_SUPERADMIN,
 * INVALID_TAB/INVALID_LEVEL) are surfaced inline. On success the dialog closes
 * and asks the parent to refresh. Authorization is enforced server-side; this
 * dialog is a convenience layer.
 */
function EditUserDialog({ open, user, onClose, onSaved }) {
  const [role, setRole] = useState('Staff');
  const [permissions, setPermissions] = useState(() => defaultStaffPermissions());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const roleRef = useRef(null);

  const originalRole = user?.role || 'Staff';

  // Seed the form from the selected user each time the dialog (re)opens so a
  // previous edit's values or error never leak into a fresh one.
  useEffect(() => {
    if (!open || !user) return;
    setRole(ROLE_OPTIONS.includes(user.role) ? user.role : 'Staff');
    setPermissions(normalizeStaffPermissions(user.permissions));
    setSubmitting(false);
    setFormError('');
    try {
      window.setTimeout(() => roleRef.current?.focus?.(), 50);
    } catch {
      // ignore
    }
  }, [open, user]);

  if (!open || !user) return null;

  const target = user.username;
  const setTabLevel = (tab, level) => {
    setPermissions((prev) => ({ ...prev, [tab]: level }));
  };

  const roleChanged = role !== originalRole;
  const permissionsChanged =
    role === 'Staff' && !permissionsEqual(permissions, normalizeStaffPermissions(user.permissions));
  const hasChanges = roleChanged || permissionsChanged;

  const authHeader = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  // Parses the JSON body of a fetch Response, tolerating an empty/non-JSON body.
  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!target) {
      setFormError('This user is missing a username and cannot be edited.');
      return;
    }

    if (!hasChanges) {
      onClose();
      return;
    }

    try {
      setSubmitting(true);

      if (!API_BASE_URL) {
        throw new Error('Admin API base URL is not configured');
      }

      const headers = await authHeader();
      const encodedTarget = encodeURIComponent(target);

      // Role change goes through PUT .../role. For a Staff target the role
      // endpoint also applies the permission set, so a single call covers both
      // the role and grants (design §3, Req 11.3).
      if (roleChanged) {
        const body = { role };
        if (role === 'Staff') {
          body.permissions = permissions;
        }
        const response = await fetch(`${API_BASE_URL}/admin/users/${encodedTarget}/role`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const data = await readJson(response);
          setFormError(describeEditError(data?.code, data?.error));
          return;
        }
      } else if (permissionsChanged) {
        // Role unchanged and still Staff: only the grant map changed, so use the
        // dedicated permissions endpoint (Req 11.4–11.6).
        const response = await fetch(`${API_BASE_URL}/admin/users/${encodedTarget}/permissions`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ permissions }),
        });
        if (!response.ok) {
          const data = await readJson(response);
          setFormError(describeEditError(data?.code, data?.error));
          return;
        }
      }

      onSaved();
    } catch (err) {
      console.error('Error updating user:', err);
      setFormError(err.message || 'Failed to save changes');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="um-modal-overlay" role="dialog" aria-modal="true" aria-label="Edit user">
      <div className="um-modal">
        <form onSubmit={handleSubmit}>
          <div className="um-modal-header">
            <h3>Edit User</h3>
            <button
              type="button"
              className="um-modal-close"
              onClick={onClose}
              aria-label="Close"
              disabled={submitting}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <div className="um-modal-body">
            {formError && (
              <div className="alert alert-error" role="alert" aria-live="assertive">
                <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
                {formError}
              </div>
            )}

            <div className="um-field">
              <span className="um-field-static-label">Email</span>
              <span className="um-field-static-value">{user.email || '—'}</span>
            </div>

            <div className="um-field">
              <label htmlFor="um-edit-role">Role</label>
              <select
                id="um-edit-role"
                ref={roleRef}
                value={role}
                onChange={(e) => setRole(e.target.value)}
                disabled={submitting}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <p className="um-field-hint">
                {role === 'Staff'
                  ? 'Staff users only access the tabs you grant below.'
                  : `${role}s have full access to all tabs${
                      role === 'Admin' ? ' except User Management' : ''
                    }.`}
              </p>
            </div>

            {role === 'Staff' && (
              <PermissionMatrix
                permissions={permissions}
                onChange={setTabLevel}
                disabled={submitting}
                namePrefix="edit-perm"
              />
            )}
          </div>

          <div className="um-modal-footer">
            <button
              type="button"
              className="um-btn um-btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="um-btn um-btn-primary"
              disabled={submitting || !hasChanges}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * DeleteUserDialog
 *
 * Confirmation modal gating the hard-delete action (Req 13.1, 13.2). Hard delete
 * is irreversible, so the destructive call only fires after the Superadmin
 * explicitly confirms here; the parent then sends DELETE /admin/users/{username}
 * with { confirm: true }. Reuses the shared um-modal chrome; the confirm button
 * uses the danger variant. Busy/error state is owned by the parent row so the
 * modal stays a controlled, presentational component.
 */
function DeleteUserDialog({ open, user, busy, error, onCancel, onConfirm }) {
  if (!open || !user) return null;

  return (
    <div className="um-modal-overlay" role="dialog" aria-modal="true" aria-label="Delete user">
      <div className="um-modal">
        <div className="um-modal-header">
          <h3>Delete User</h3>
          <button
            type="button"
            className="um-modal-close"
            onClick={onCancel}
            aria-label="Close"
            disabled={busy}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <div className="um-modal-body">
          {error && (
            <div className="alert alert-error" role="alert" aria-live="assertive">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
              {error}
            </div>
          )}
          <p className="um-delete-warning">
            Are you sure you want to permanently delete{' '}
            <strong>{user.email || user.username}</strong>? This removes their account and access
            for this tenant and cannot be undone.
          </p>
        </div>

        <div className="um-modal-footer">
          <button
            type="button"
            className="um-btn um-btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="um-btn um-btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Deleting…
              </>
            ) : (
              'Delete User'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * UserRowActions
 *
 * Per-row Actions cell: Edit (role + permissions, task 11.4), a Disable/Enable
 * toggle, and a Delete control behind a confirmation modal (task 11.5).
 *
 * Wiring (design §3, Req 12.1/12.5, 13.1/13.2):
 *  - The toggle calls POST /admin/users/{username}/disable or .../enable based
 *    on the user's current enabled status, then refreshes the list on success.
 *  - Delete opens DeleteUserDialog; only on explicit confirmation does it call
 *    DELETE /admin/users/{username} with { confirm: true }.
 *
 * Safety-rail and validation errors (SELF_MODIFICATION, LAST_SUPERADMIN,
 * CONFIRM_REQUIRED) are surfaced inline. Authorization is enforced server-side;
 * these controls are a convenience layer.
 */
function UserRowActions({ user, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const enabled = user.enabled !== false;
  const target = user.username;

  const authHeader = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  // Parses the JSON body of a fetch Response, tolerating an empty/non-JSON body.
  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Disable or enable the account based on its current status (Req 12.1, 12.5).
  const handleToggle = async () => {
    setActionError('');

    if (!target) {
      setActionError('This user is missing a username and cannot be updated.');
      return;
    }

    try {
      setBusy(true);

      if (!API_BASE_URL) {
        throw new Error('Admin API base URL is not configured');
      }

      const headers = await authHeader();
      const action = enabled ? 'disable' : 'enable';
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${encodeURIComponent(target)}/${action}`,
        { method: 'POST', headers }
      );

      if (!response.ok) {
        const data = await readJson(response);
        setActionError(describeActionError(data?.code, data?.error));
        return;
      }

      onChanged();
    } catch (err) {
      console.error('Error updating user status:', err);
      setActionError(err.message || 'Failed to update the user');
    } finally {
      setBusy(false);
    }
  };

  // Hard delete, only ever reached after explicit confirmation in the modal.
  // Sends { confirm: true } to satisfy the backend's CONFIRM_REQUIRED rail
  // (Req 13.1, 13.2).
  const handleDelete = async () => {
    setActionError('');

    if (!target) {
      setActionError('This user is missing a username and cannot be deleted.');
      setConfirmOpen(false);
      return;
    }

    try {
      setBusy(true);

      if (!API_BASE_URL) {
        throw new Error('Admin API base URL is not configured');
      }

      const headers = await authHeader();
      const response = await fetch(`${API_BASE_URL}/admin/users/${encodeURIComponent(target)}`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ confirm: true }),
      });

      if (!response.ok) {
        const data = await readJson(response);
        setActionError(describeActionError(data?.code, data?.error));
        setConfirmOpen(false);
        return;
      }

      setConfirmOpen(false);
      onChanged();
    } catch (err) {
      console.error('Error deleting user:', err);
      setActionError(err.message || 'Failed to delete the user');
      setConfirmOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="um-row-actions">
        <button
          type="button"
          className="um-btn um-btn-secondary um-btn-sm"
          onClick={() => onEdit(user)}
          disabled={busy}
        >
          <i className="fas fa-pen" aria-hidden="true"></i>
          Edit
        </button>
        <button
          type="button"
          className="um-btn um-btn-secondary um-btn-sm"
          onClick={handleToggle}
          disabled={busy}
        >
          <i
            className={`fas fa-${enabled ? 'user-slash' : 'user-check'}`}
            aria-hidden="true"
          ></i>
          {enabled ? 'Disable' : 'Enable'}
        </button>
        <button
          type="button"
          className="um-btn um-btn-danger um-btn-sm"
          onClick={() => {
            setActionError('');
            setConfirmOpen(true);
          }}
          disabled={busy}
        >
          <i className="fas fa-trash" aria-hidden="true"></i>
          Delete
        </button>
      </div>

      {actionError && (
        <div className="um-row-error" role="alert" aria-live="assertive">
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
          {actionError}
        </div>
      )}

      <DeleteUserDialog
        open={confirmOpen}
        user={user}
        busy={busy}
        error={actionError}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const statusRef = useRef(null);
  const capability = useAdminCapability();

  const [username, setUsername] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!API_BASE_URL) {
        throw new Error('Admin API base URL is not configured');
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_BASE_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load users');
      }

      const data = await response.json();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Establish the signed-in identity for the header. Authorization is enforced
  // server-side; the gating below is convenience only (design §6, Req 5/6).
  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUsername(user.username || user.attributes?.email || '');
      } catch (err) {
        console.error('Auth error (user management):', err);
        navigate('/admin/login');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Superadmin-only view. Fail closed: once the capability resolves, anyone who
  // is not a Superadmin is redirected away. While the capability is loading we
  // hold off so we never flash the page or redirect a valid Superadmin.
  useEffect(() => {
    if (capability.loading) return;
    if (!capability.isSuperadmin) {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    fetchUsers();
  }, [capability.loading, capability.isSuperadmin, fetchUsers, navigate]);

  useEffect(() => {
    if (!error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [error]);

  const handleUserCreated = useCallback(() => {
    setAddOpen(false);
    fetchUsers();
  }, [fetchUsers]);

  const handleUserSaved = useCallback(() => {
    setEditUser(null);
    fetchUsers();
  }, [fetchUsers]);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
      setError('Failed to log out');
    }
  };

  // Block-render while we confirm Superadmin so non-Superadmins never see the
  // list, even briefly, before the redirect effect runs.
  if (capability.loading || (!capability.isSuperadmin && loading)) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading user management...</p>
      </div>
    );
  }

  if (!capability.isSuperadmin) {
    return null;
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-users-cog"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>User Management</h1>
            <p>Manage users, roles, and tab permissions in your tenant</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error && (
          <div
            className="alert alert-error"
            ref={statusRef}
            tabIndex={-1}
            role="alert"
            aria-live="assertive"
          >
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <div className="products-list-container">
          {/* Toolbar. The "Add User" action (task 11.3) mounts here; per-row
              edit / disable-enable / delete controls (tasks 11.4–11.5) attach to
              an Actions column added to the table below. This list view leaves a
              clean boundary for those without implementing them. */}
          <div className="um-toolbar">
            <h2>Users</h2>
            <div className="um-toolbar-actions">
              <button
                type="button"
                className="um-btn um-btn-primary"
                onClick={() => setAddOpen(true)}
              >
                <i className="fas fa-user-plus" aria-hidden="true"></i>
                Add User
              </button>
            </div>
          </div>

          {loading ? (
            <div className="admin-loading" style={{ minHeight: '120px' }}>
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <p>No users found</p>
            </div>
          ) : (
            <div className="um-table-wrapper">
              <table className="um-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Permissions</th>
                    <th className="um-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => {
                    const enabled = user.enabled !== false;
                    return (
                      <tr key={user.username || user.email || idx}>
                        <td className="um-email">{user.email || '—'}</td>
                        <td>
                          <span className={`um-role-badge um-role-${String(user.role || '').toLowerCase()}`}>
                            {user.role || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <span className={`um-status um-status-${enabled ? 'enabled' : 'disabled'}`}>
                            <i
                              className={`fas fa-circle`}
                              aria-hidden="true"
                            ></i>
                            {enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td>
                          <StaffPermissionSummary role={user.role} permissions={user.permissions} />
                        </td>
                        <td className="um-actions-cell">
                          {/* Per-row actions: Edit (role + permissions, task
                              11.4) plus the Disable/Enable toggle and Delete
                              confirmation flow (task 11.5). */}
                          <UserRowActions
                            user={user}
                            onEdit={setEditUser}
                            onChanged={fetchUsers}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <AddUserDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={handleUserCreated}
      />

      <EditUserDialog
        open={!!editUser}
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={handleUserSaved}
      />
    </div>
  );
};

export default AdminUserManagement;
